import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { motion } from 'framer-motion';
import DeleteActiveListDialog from '@/components/DeleteActiveListDialog';
import { SortableTaskItem, ListHeaderCell, ListBodyCell } from '@/components/ActiveListColumn';
import { focusOpacity, focusBlur } from '@/utils/focusMode';
import ActiveActionBar from '@/components/ActiveActionBar';
import { useSettings } from '@/contexts/SettingsContext';
import { reorderWithinList } from '@/utils/activeListReorder';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Modern browsers can drive the header's mirrored transform purely via a
// CSS scroll-timeline (zero JS on scroll, no dispatch lag). Detected once;
// unsupported browsers keep using the JS-driven transform as before.
const SUPPORTS_SCROLL_TIMELINE =
  typeof CSS !== 'undefined' && !!CSS.supports && CSS.supports('animation-timeline: scroll()');

export default function ActivePage({ tasks, fetchTasks, tags, dragHandlePosition, onAddTaskForList }) {
  const { assigneeTagsEnabled, contextsEnabled, contexts, activeLists, setActiveLists, fetchActiveLists, loading: settingsLoading } = useSettings();
  const [activeTasks, setActiveTasks] = useState([]);
  const [focusMode, setFocusMode] = useState(() => {
    // Load from localStorage or default to false
    return localStorage.getItem('activeFocusMode') === 'true';
  });
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);
  const [assigneeFilter, setAssigneeFilter] = useState('any-user');
  const [contextFilter, setContextFilter] = useState('any-context');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Inline list rename state: { id, value } when renaming
  const [renamingListId, setRenamingListId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  // Delete list dialog state
  const [listToDelete, setListToDelete] = useState(null);
  // Superfocus: when set to a list id, all other lists are hidden and this
  // list's header + body expand to the full page width. Persisted across
  // navigation/reloads so returning to this page keeps the same list focused.
  const [superfocusListId, setSuperfocusListId] = useState(() => {
    return localStorage.getItem('activeSuperfocusListId') || null;
  });
  // Horizontal scroll ref for the lists container (scroll to end when a new list is added)
  const listsContainerRef = useRef(null);
  const headerStripRef = useRef(null);
  const fixedScrollbarRef = useRef(null);
  const toggleSuperfocus = (listId) => {
    // Reset the header's mirrored-scroll transform and both scroll positions
    // so the header strip and body row stay aligned when switching in/out
    // of Superfocus, even if the lists were previously scrolled horizontally.
    if (headerStripRef.current) headerStripRef.current.style.transform = '';
    if (listsContainerRef.current) listsContainerRef.current.scrollLeft = 0;
    if (fixedScrollbarRef.current) fixedScrollbarRef.current.scrollLeft = 0;
    setSuperfocusListId((prev) => (prev === listId ? null : listId));
  };
  // Dynamic horizontal padding so the left-most list aligns with the "Active Tasks"
  // title, and the right-most list can be scrolled all the way to that same alignment.
  const [horizontalPadding, setHorizontalPadding] = useState('1rem');
  const [horizontalRightPadding, setHorizontalRightPadding] = useState('1rem');
  // Tracks the body row's scrollable width so the fixed bottom scrollbar matches.
  const [scrollState, setScrollState] = useState({ scrollWidth: 0, clientWidth: 0 });

  // Save focusMode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('activeFocusMode', focusMode.toString());
  }, [focusMode]);

  // Save superfocusListId to localStorage so it persists across navigation
  useEffect(() => {
    if (superfocusListId) {
      localStorage.setItem('activeSuperfocusListId', superfocusListId);
    } else {
      localStorage.removeItem('activeSuperfocusListId');
    }
  }, [superfocusListId]);

  // Exit superfocus if the currently-focused list no longer exists (e.g.
  // deleted) — but only once activeLists has actually finished loading,
  // otherwise the initial empty [] before the fetch resolves would wrongly
  // clear a persisted superfocus selection on every page load/reload.
  useEffect(() => {
    if (settingsLoading) return;
    if (superfocusListId && !(activeLists || []).find((l) => l.id === superfocusListId)) {
      setSuperfocusListId(null);
    }
  }, [activeLists, superfocusListId, settingsLoading]);

  // Track the body row's scrollWidth so the fixed bottom scrollbar can match it.
  useEffect(() => {
    const hasMultiple = (activeLists?.length || 0) > 1;
    if (!hasMultiple) return;
    const body = listsContainerRef.current;
    if (!body) return;
    const update = () => {
      setScrollState({
        scrollWidth: body.scrollWidth,
        clientWidth: body.clientWidth,
      });
    };
    update();
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(update);
    ro.observe(body);
    return () => {
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
  }, [activeLists]);

  // For browsers supporting CSS scroll-timeline: keep the header's animation
  // endpoint (--scroll-distance) in sync with the actual scrollable range,
  // so `translateX(-scroll-distance)` at 100% scroll progress exactly
  // matches the body row's max scrollLeft.
  useEffect(() => {
    if (!SUPPORTS_SCROLL_TIMELINE || !headerStripRef.current) return;
    const distance = Math.max(0, scrollState.scrollWidth - scrollState.clientWidth);
    headerStripRef.current.style.setProperty('--scroll-distance', `${distance}px`);
  }, [scrollState]);

  // Dynamic horizontal padding: left-most list aligns with the "Active Tasks"
  // page header; right-most list, at max scroll, stops at that same line
  // (its left edge cannot scroll past the header's left edge).
  // Also tracks the action bar's bottom offset so list headers can stick just
  // below it.
  const [listHeaderTop, setListHeaderTop] = useState(148);
  useEffect(() => {
    const hasMultiple = (activeLists?.length || 0) > 1;
    if (!hasMultiple) return;
    const compute = () => {
      const pageTitle = document.querySelector('[data-testid="active-page"] h1');
      if (!pageTitle) return;
      const titleRect = pageTitle.getBoundingClientRect();
      setHorizontalPadding(`${titleRect.left}px`);
      // Column width on desktop; on mobile each column is ~viewport-wide so
      // horizontal scrolling pads naturally.
      const viewportWidth = window.innerWidth;
      const columnWidth = viewportWidth >= 640 ? 420 : viewportWidth - 48;
      const rightPadding = viewportWidth - columnWidth - titleRect.left;
      setHorizontalRightPadding(`${Math.max(16, rightPadding)}px`);
      // Measure the sticky top bar + action bar bottom so the list headers
      // can freeze directly below it.
      const actionBar = document.querySelector('[data-testid="active-page"] > div.sticky');
      if (actionBar) {
        // Action bar is sticky at top:60px. Its bottom (when sticky) = 60 + height.
        const topBarOffset = 60;
        setListHeaderTop(topBarOffset + actionBar.offsetHeight);
      }
    };
    const t = setTimeout(compute, 100);
    window.addEventListener('resize', compute);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', compute);
    };
  }, [activeLists]);

  // Assignee tags sorted by priority
  const assigneeTags = tags
    .filter((t) => t.category === 'assignee')
    .sort((a, b) => a.priority - b.priority);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 0,
      },
    })
  );

  // Build dynamic priority maps from tags (including context priority for cross-context sorting)
  const buildPriorityMaps = () => {
    const typePriorityMap = {};
    const domainPriorityMap = {};
    const energyPriorityMap = {};
    const contextPriorityMap = {};
    
    // Build context priority map
    contexts.forEach((ctx) => {
      contextPriorityMap[ctx.id] = ctx.priority;
    });
    
    tags.forEach((tag) => {
      const tagKey = `${tag.emoji} ${tag.name}`;
      // For cross-context sorting, we need to consider context priority
      // Tags from higher priority contexts should come first
      const contextMultiplier = tag.context_id ? (contextPriorityMap[tag.context_id] || 999) : 0;
      const adjustedPriority = contextMultiplier * 1000 + tag.priority;
      
      if (tag.category === 'Type') {
        typePriorityMap[tagKey] = { priority: tag.priority, contextPriority: contextMultiplier, combined: adjustedPriority };
      } else if (tag.category === 'Domain') {
        domainPriorityMap[tagKey] = { priority: tag.priority, contextPriority: contextMultiplier, combined: adjustedPriority };
      } else if (tag.category === 'Energy') {
        energyPriorityMap[tagKey] = { priority: tag.priority, contextPriority: contextMultiplier, combined: adjustedPriority };
      }
    });
    
    return { typePriorityMap, domainPriorityMap, energyPriorityMap, contextPriorityMap };
  };

  const { typePriorityMap, domainPriorityMap, energyPriorityMap, contextPriorityMap } = buildPriorityMaps();

  // Legacy simple priority maps for backward compatibility
  const simplePriorityMaps = () => {
    const type = {};
    const domain = {};
    const energy = {};
    
    tags.forEach((tag) => {
      const tagKey = `${tag.emoji} ${tag.name}`;
      if (tag.category === 'Type') {
        type[tagKey] = tag.priority;
      } else if (tag.category === 'Domain') {
        domain[tagKey] = tag.priority;
      } else if (tag.category === 'Energy') {
        energy[tagKey] = tag.priority;
      }
    });
    
    return { type, domain, energy };
  };

  const simpleTypePriorityMap = simplePriorityMaps().type;
  const simpleDomainPriorityMap = simplePriorityMaps().domain;
  const simpleEnergyPriorityMap = simplePriorityMaps().energy;

  useEffect(() => {
    // Filter active tasks that are not completed
    let filtered = tasks.filter((t) => t.is_active && !t.completed);
    
    // Apply context filter for Active page
    if (contextsEnabled && contextFilter !== 'any-context') {
      filtered = filtered.filter(t => t.context_id === contextFilter);
    }
    
    // Sort by manual_order first (if exists), then by tag priority for tasks without manual order
    filtered.sort((a, b) => {
      // Tasks with manual_order come first, sorted by their order
      const hasOrderA = a.manual_order !== null && a.manual_order !== undefined;
      const hasOrderB = b.manual_order !== null && b.manual_order !== undefined;
      
      // Both have manual order - sort by it
      if (hasOrderA && hasOrderB) {
        return a.manual_order - b.manual_order;
      }
      
      // Only A has manual order - A comes first
      if (hasOrderA && !hasOrderB) {
        return -1;
      }
      
      // Only B has manual order - B comes first
      if (!hasOrderA && hasOrderB) {
        return 1;
      }
      
      // Neither has manual order - fall back to tag priority sort
      // In "Any Context" mode with contexts enabled, sort by context priority first
      if (contextsEnabled && contextFilter === 'any-context') {
        const contextA = contextPriorityMap[a.context_id] || 999;
        const contextB = contextPriorityMap[b.context_id] || 999;
        
        // Use combined context + tag priority
        const typeA = typePriorityMap[a.tag_type]?.combined || 999999;
        const typeB = typePriorityMap[b.tag_type]?.combined || 999999;
        if (typeA !== typeB) return typeA - typeB;
        
        const domainA = domainPriorityMap[a.tag_domain]?.combined || 999999;
        const domainB = domainPriorityMap[b.tag_domain]?.combined || 999999;
        if (domainA !== domainB) return domainA - domainB;
        
        const energyA = energyPriorityMap[a.tag_energy]?.combined || 999999;
        const energyB = energyPriorityMap[b.tag_energy]?.combined || 999999;
        return energyA - energyB;
      }
      
      // Standard priority sort using simple maps
      const typeA = simpleTypePriorityMap[a.tag_type] || 999;
      const typeB = simpleTypePriorityMap[b.tag_type] || 999;
      if (typeA !== typeB) return typeA - typeB;
      
      const domainA = simpleDomainPriorityMap[a.tag_domain] || 999;
      const domainB = simpleDomainPriorityMap[b.tag_domain] || 999;
      if (domainA !== domainB) return domainA - domainB;
      
      const energyA = simpleEnergyPriorityMap[a.tag_energy] || 999;
      const energyB = simpleEnergyPriorityMap[b.tag_energy] || 999;
      return energyA - energyB;
    });
    
    // Apply assignee filter - "any-user" shows all tasks
    if (assigneeTagsEnabled && assigneeFilter && assigneeFilter !== 'any-user') {
      filtered = filtered.filter(task => task.assignee_tag === assigneeFilter);
    }
    
    setActiveTasks(filtered);
  }, [tasks, tags, assigneeFilter, assigneeTagsEnabled, contextsEnabled, contextFilter, contexts]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeType = active.data?.current?.type;

    // 1) Horizontal list-column reorder
    if (activeType === 'list-column') {
      if (active.id === over.id) return;
      const overType = over.data?.current?.type;
      if (overType !== 'list-column') return;
      const oldIndex = activeLists.findIndex((l) => `column-${l.id}` === active.id);
      const newIndex = activeLists.findIndex((l) => `column-${l.id}` === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(activeLists, oldIndex, newIndex);
      // Optimistic update
      setActiveLists(reordered);
      try {
        await axios.post(
          `${API}/active_lists/reorder`,
          { list_ids: reordered.map((l) => l.id) },
          { withCredentials: true }
        );
        // Refresh canonical order (with updated 'order' values)
        await fetchActiveLists();
      } catch (error) {
        console.error('Error reordering lists:', error);
        toast.error('Failed to reorder lists');
        await fetchActiveLists();
      }
      return;
    }

    // 2) Task drag (within a list or across lists)
    const draggedTask = activeTasks.find((t) => t.id === active.id);
    if (!draggedTask) return;

    let targetListId = null;
    let targetTaskId = null;
    if (typeof over.id === 'string' && over.id.startsWith('list-drop-')) {
      // Dropped onto a list container (empty area)
      targetListId = over.id.replace('list-drop-', '');
    } else if (typeof over.id === 'string' && over.id.startsWith('column-')) {
      // Dropped onto a list-column wrapper (catch-all, e.g. empty column body)
      targetListId = over.id.replace('column-', '');
    } else {
      const overTask = activeTasks.find((t) => t.id === over.id);
      if (!overTask) return;
      targetListId = overTask.active_list_id;
      targetTaskId = overTask.id;
    }

    const sourceListId = draggedTask.active_list_id;

    // Split tasks into lists
    const byList = {};
    activeTasks.forEach((t) => {
      const lid = t.active_list_id || 'none';
      if (!byList[lid]) byList[lid] = [];
      byList[lid].push(t);
    });

    if (sourceListId === targetListId) {
      // Reorder within same list
      if (active.id === over.id) return;
      const listTasks = byList[sourceListId] || [];
      const oldIndex = listTasks.findIndex((t) => t.id === active.id);
      const newIndex = targetTaskId
        ? listTasks.findIndex((t) => t.id === targetTaskId)
        : listTasks.length - 1;
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(listTasks, oldIndex, newIndex);
      byList[sourceListId] = reordered;

      // Update state optimistically
      const newActiveTasks = Object.values(byList).flat();
      setActiveTasks(newActiveTasks);

      try {
        await axios.post(
          `${API}/tasks/reorder`,
          { task_ids: reordered.map((t) => t.id) },
          { withCredentials: true }
        );
      } catch (error) {
        console.error('Error saving order:', error);
        toast.error('Failed to save task order');
      }
      return;
    }

    // Cross-list move
    const updatedDragged = { ...draggedTask, active_list_id: targetListId };
    const sourceList = (byList[sourceListId] || []).filter((t) => t.id !== draggedTask.id);
    const targetList = [...(byList[targetListId] || [])];
    let insertAt = targetList.length;
    if (targetTaskId) {
      const idx = targetList.findIndex((t) => t.id === targetTaskId);
      insertAt = idx >= 0 ? idx : targetList.length;
    }
    targetList.splice(insertAt, 0, updatedDragged);
    byList[sourceListId] = sourceList;
    byList[targetListId] = targetList;

    const newActiveTasks = Object.values(byList).flat();
    setActiveTasks(newActiveTasks);

    try {
      await axios.patch(
        `${API}/tasks/${draggedTask.id}`,
        { active_list_id: targetListId },
        { withCredentials: true }
      );
      await axios.post(
        `${API}/tasks/reorder`,
        { task_ids: targetList.map((t) => t.id) },
        { withCredentials: true }
      );
      if (sourceList.length > 0) {
        await axios.post(
          `${API}/tasks/reorder`,
          { task_ids: sourceList.map((t) => t.id) },
          { withCredentials: true }
        );
      }
      fetchTasks();
    } catch (error) {
      console.error('Error moving task between lists:', error);
      toast.error('Failed to move task');
      fetchTasks();
    }
  };

  const handleAutoPopulate = async (listId) => {
    try {
      const params = listId ? `?list_id=${listId}` : '';
      const response = await axios.post(`${API}/tasks/auto-populate${params}`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      fetchTasks();
    } catch (error) {
      console.error('Error auto-populating:', error);
      toast.error('Failed to auto-populate tasks');
    }
  };

  const handleReSort = async (listId) => {
    try {
      const params = listId ? `?list_id=${listId}` : '';
      // Call backend to clear all manual_order values (optionally scoped to list)
      await axios.post(`${API}/tasks/reset-order${params}`, {}, {
        withCredentials: true
      });
      toast.success('Tasks re-sorted by priority');
      fetchTasks();
    } catch (error) {
      console.error('Error re-sorting tasks:', error);
      toast.error('Failed to re-sort tasks');
    }
  };

  // When called from a list's kabab, clear only that list's tasks.
  const [clearAllListId, setClearAllListId] = useState(null);
  const handleClearAll = (listId) => {
    setClearAllListId(listId || null);
    setShowClearAllDialog(true);
  };

  const getListTasks = (listId) =>
    activeTasks.filter((t) => (t.active_list_id || null) === listId);

  const handleMoveToTop = (taskId) =>
    reorderWithinList({
      taskId,
      activeTasks,
      setActiveTasks,
      successMessage: 'Task moved to top',
      computeNewOrder: (listTasks, currentIndex) => {
        if (currentIndex === 0) return null;
        const t = listTasks[currentIndex];
        return [t, ...listTasks.filter((_, i) => i !== currentIndex)];
      },
    });

  const handleMoveToBottom = (taskId) =>
    reorderWithinList({
      taskId,
      activeTasks,
      setActiveTasks,
      successMessage: 'Task moved to bottom',
      computeNewOrder: (listTasks, currentIndex) => {
        if (currentIndex === listTasks.length - 1) return null;
        const t = listTasks[currentIndex];
        return [...listTasks.filter((_, i) => i !== currentIndex), t];
      },
    });

  const handleMoveUp = (taskId) =>
    reorderWithinList({
      taskId,
      activeTasks,
      setActiveTasks,
      successMessage: 'Task moved up',
      computeNewOrder: (listTasks, currentIndex) => {
        if (currentIndex <= 0) return null;
        const next = [...listTasks];
        [next[currentIndex - 1], next[currentIndex]] = [next[currentIndex], next[currentIndex - 1]];
        return next;
      },
    });

  const handleMoveDown = (taskId) =>
    reorderWithinList({
      taskId,
      activeTasks,
      setActiveTasks,
      successMessage: 'Task moved down',
      computeNewOrder: (listTasks, currentIndex) => {
        if (currentIndex >= listTasks.length - 1) return null;
        const next = [...listTasks];
        [next[currentIndex], next[currentIndex + 1]] = [next[currentIndex + 1], next[currentIndex]];
        return next;
      },
    });

  const handleAddNewList = async () => {
    try {
      await axios.post(
        `${API}/active_lists`,
        {},
        { withCredentials: true }
      );
      await fetchActiveLists();
      toast.success('New list added');
      // Scroll to end of horizontal container on next frame
      setTimeout(() => {
        if (listsContainerRef.current) {
          listsContainerRef.current.scrollTo({
            left: listsContainerRef.current.scrollWidth,
            behavior: 'smooth',
          });
        }
      }, 80);
    } catch (error) {
      console.error('Error creating list:', error);
      toast.error(error.response?.data?.detail || 'Failed to create list');
    }
  };

  const startRenameList = (list) => {
    setRenamingListId(list.id);
    setRenameValue(list.name);
  };

  const commitRenameList = async () => {
    if (!renamingListId) return;
    const value = renameValue.trim();
    const original = activeLists.find((l) => l.id === renamingListId);
    if (!value || (original && original.name === value)) {
      setRenamingListId(null);
      setRenameValue('');
      return;
    }
    try {
      await axios.patch(
        `${API}/active_lists/${renamingListId}`,
        { name: value },
        { withCredentials: true }
      );
      await fetchActiveLists();
    } catch (error) {
      console.error('Error renaming list:', error);
      toast.error('Failed to rename list');
    } finally {
      setRenamingListId(null);
      setRenameValue('');
    }
  };

  const confirmClearAll = async () => {
    try {
      const toClear = clearAllListId
        ? activeTasks.filter((t) => t.active_list_id === clearAllListId)
        : activeTasks;
      for (const task of toClear) {
        await axios.patch(`${API}/tasks/${task.id}`, { 
          is_active: false,
          manually_deactivated: true,
          activation_date: null
        }, {
          withCredentials: true
        });
      }
      toast.success(
        clearAllListId
          ? 'Cleared tasks from this list'
          : 'All tasks cleared from Active list'
      );
      setShowClearAllDialog(false);
      setClearAllListId(null);
      fetchTasks();
    } catch (error) {
      console.error('Error clearing tasks:', error);
      toast.error('Failed to clear tasks');
    }
  };

  // Group tasks by active_list_id (preserving current activeTasks sort order)
  const hasMultipleLists = (activeLists?.length || 0) > 1;
  const tasksByList = {};
  (activeLists || []).forEach((l) => { tasksByList[l.id] = []; });
  activeTasks.forEach((t) => {
    const lid = t.active_list_id;
    if (lid && tasksByList[lid]) tasksByList[lid].push(t);
  });
  // When superfocused, only the selected list is rendered (header + body).
  const visibleLists = superfocusListId
    ? (activeLists || []).filter((l) => l.id === superfocusListId)
    : activeLists || [];

  return (
    <main className="pb-6 sm:pb-8" data-testid="active-page">
      <div className={`sticky top-[60px] z-20 pt-2 md:pt-6 pb-4 bg-slate-900 ${hasMultipleLists ? '' : 'mb-6'}`}>
        <ActiveActionBar
          focusMode={focusMode}
          setFocusMode={setFocusMode}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          assigneeFilter={assigneeFilter}
          setAssigneeFilter={setAssigneeFilter}
          contextFilter={contextFilter}
          setContextFilter={setContextFilter}
          activeTasksCount={activeTasks.length}
          assigneeTagsEnabled={assigneeTagsEnabled}
          assigneeTags={assigneeTags}
          contextsEnabled={contextsEnabled}
          contexts={contexts}
          hasMultipleLists={hasMultipleLists}
          handleAutoPopulate={handleAutoPopulate}
          handleReSort={handleReSort}
          handleClearAll={handleClearAll}
          handleAddNewList={handleAddNewList}
        />
      </div>

      {/* Desktop gradient overlays - fade from content edges to window edges */}
      <div className="hidden md:block pointer-events-none">
        {/* Left gradient */}
        <div
          className="fixed left-0 top-[60px] bottom-0 z-30"
          style={{
            width: `calc((100vw - 48rem) / 2)`,
            background: 'linear-gradient(to right, rgb(15 23 42) 0%, transparent 100%)',
          }}
        />
        {/* Right gradient */}
        <div
          className="fixed right-0 top-[60px] bottom-0 z-30"
          style={{
            width: `calc((100vw - 48rem) / 2)`,
            background: 'linear-gradient(to left, rgb(15 23 42) 0%, transparent 100%)',
          }}
        />
      </div>

      <div className={`${hasMultipleLists ? 'max-w-full' : 'max-w-3xl mx-auto px-4 sm:px-6'} active-lists-timeline-scope`}>
        {activeTasks.length === 0 && !hasMultipleLists && (
        <div
          className="text-center py-16 px-4 rounded-2xl"
          style={{ backgroundColor: 'var(--color-secondary)' }}
          data-testid="empty-active-state"
        >
          <p className="text-lg text-muted-foreground">
            No active tasks. Use "Auto Fill" or activate tasks from Storage.
          </p>
        </div>
      )}

      {!hasMultipleLists && activeTasks.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={activeTasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
            {!focusMode && (
              <div className="space-y-3" data-testid="active-tasks-list">
                {activeTasks.map((task) => (
                  <SortableTaskItem
                    key={task.id}
                    task={task}
                    onComplete={fetchTasks}
                    onUpdate={fetchTasks}
                    tags={tags}
                    dragHandlePosition={dragHandlePosition}
                    onMoveToTop={handleMoveToTop}
                    onMoveToBottom={handleMoveToBottom}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                  />
                ))}
              </div>
            )}

            {focusMode && (
              <div className="space-y-3">
                {activeTasks.map((task, index) => {
                  const opacity = focusOpacity(index);
                  const blur = focusBlur(index);
                  return (
                    <div
                      key={task.id}
                      style={{
                        opacity,
                        filter: `blur(${blur}px)`,
                        transition: 'all 0.3s ease-in-out',
                      }}
                    >
                      <SortableTaskItem
                        task={task}
                        onComplete={fetchTasks}
                        onUpdate={fetchTasks}
                        tags={tags}
                        dragHandlePosition={dragHandlePosition}
                        onMoveToTop={handleMoveToTop}
                        onMoveToBottom={handleMoveToBottom}
                        onMoveUp={handleMoveUp}
                        onMoveDown={handleMoveDown}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </SortableContext>
        </DndContext>
      )}

      {hasMultipleLists && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          {/* Sticky headers strip — frozen at top of window while page scrolls.
              Header doesn't natively scroll; its inner row is translated
              horizontally to mirror the body row's scrollLeft. On browsers
              supporting CSS scroll-timeline (Chrome/Edge/Safari 17.4+), this
              is driven entirely by a compositor-side CSS animation (zero JS,
              zero scroll-event-dispatch lag). Other browsers fall back to a
              JS-driven transform + will-change for GPU-layer promotion. */}
          <div
            className="sticky z-20 bg-slate-900 overflow-hidden py-2"
            style={{ top: `${listHeaderTop}px` }}
            data-testid="active-lists-header-strip"
          >
            <div
              ref={headerStripRef}
              className={
                superfocusListId
                  ? 'max-w-3xl mx-auto px-4 sm:px-6'
                  : 'active-header-row active-lists-timeline-target flex gap-4 sm:gap-6 will-change-transform'
              }
              style={
                superfocusListId
                  ? {}
                  : {
                      paddingLeft: horizontalPadding,
                      paddingRight: horizontalRightPadding,
                    }
              }
            >
              <SortableContext
                items={visibleLists.map((l) => `column-${l.id}`)}
                strategy={horizontalListSortingStrategy}
              >
                {visibleLists.map((list) => (
                  <ListHeaderCell
                    key={list.id}
                    list={list}
                    listTasks={tasksByList[list.id] || []}
                    activeListsCount={activeLists.length}
                    dragHandlePosition={dragHandlePosition}
                    renamingListId={renamingListId}
                    renameValue={renameValue}
                    setRenameValue={setRenameValue}
                    startRenameList={startRenameList}
                    commitRenameList={commitRenameList}
                    setRenamingListId={setRenamingListId}
                    setListToDelete={setListToDelete}
                    handleAutoPopulate={handleAutoPopulate}
                    handleReSort={handleReSort}
                    handleClearAll={handleClearAll}
                    onAddTaskForList={onAddTaskForList}
                    isSuperfocused={superfocusListId === list.id}
                    onToggleSuperfocus={() => toggleSuperfocus(list.id)}
                    fullWidth={!!superfocusListId}
                  />
                ))}
              </SortableContext>
            </div>
          </div>

          {/* Body row — horizontally scrollable, scroll-synced with header strip
              and the fixed bottom scrollbar (desktop only). */}
          <div
            ref={listsContainerRef}
            className={
              superfocusListId
                ? 'max-w-3xl mx-auto px-4 sm:px-6 pb-4'
                : 'storage-horizontal-scroll active-lists-timeline-scroller flex gap-4 sm:gap-6 overflow-x-auto pb-4'
            }
            style={
              superfocusListId
                ? {}
                : {
                    WebkitOverflowScrolling: 'touch',
                    paddingLeft: horizontalPadding,
                    paddingRight: horizontalRightPadding,
                  }
            }
            onScroll={(e) => {
              const header = headerStripRef.current;
              const fixedSb = fixedScrollbarRef.current;
              const x = e.target.scrollLeft;
              if (header && !SUPPORTS_SCROLL_TIMELINE) {
                header.style.transform = `translate3d(${-x}px, 0, 0)`;
              }
              if (fixedSb && fixedSb.scrollLeft !== x) {
                fixedSb.scrollLeft = x;
              }
            }}
            data-testid="active-lists-container"
          >
            {visibleLists.map((list) => (
              <ListBodyCell
                key={list.id}
                list={list}
                listTasks={tasksByList[list.id] || []}
                focusMode={focusMode}
                onTaskComplete={fetchTasks}
                onTaskUpdate={fetchTasks}
                tags={tags}
                dragHandlePosition={dragHandlePosition}
                onMoveToTop={handleMoveToTop}
                onMoveToBottom={handleMoveToBottom}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                fullWidth={!!superfocusListId}
              />
            ))}
          </div>
        </DndContext>
      )}

      {/* Fixed horizontal scrollbar at bottom of viewport — desktop only.
          Scroll-synced with the body row + header strip. */}
      {hasMultipleLists && scrollState.scrollWidth > scrollState.clientWidth && (
        <div
          className="hidden sm:block fixed bottom-0 left-0 right-0 z-40 bg-slate-900/95 border-t border-slate-700/50 px-4 py-2"
          data-testid="active-fixed-scrollbar-container"
        >
          <div
            ref={fixedScrollbarRef}
            className="storage-fixed-scrollbar overflow-x-auto"
            onScroll={(e) => {
              const body = listsContainerRef.current;
              const x = e.target.scrollLeft;
              if (body && body.scrollLeft !== x) {
                body.scrollLeft = x;
              }
            }}
            style={{ maxWidth: '100%' }}
          >
            <div style={{ width: scrollState.scrollWidth, height: '1px' }} />
          </div>
        </div>
      )}

      <DeleteActiveListDialog
        open={!!listToDelete}
        onOpenChange={(open) => !open && setListToDelete(null)}
        listToDelete={listToDelete}
        allLists={activeLists}
        taskCount={
          listToDelete ? (tasksByList[listToDelete.id] || []).length : 0
        }
        onDeleted={async () => {
          await fetchActiveLists();
          fetchTasks();
        }}
      />

      <Dialog open={showClearAllDialog} onOpenChange={(o) => { setShowClearAllDialog(o); if (!o) setClearAllListId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear All Active Tasks?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {(() => {
              const count = clearAllListId
                ? activeTasks.filter((t) => t.active_list_id === clearAllListId).length
                : activeTasks.length;
              const listName = clearAllListId
                ? activeLists.find((l) => l.id === clearAllListId)?.name
                : null;
              return listName
                ? `This will move all ${count} tasks in "${listName}" back to storage.`
                : `This will move all ${count} active tasks back to storage.`;
            })()}
          </p>
          <DialogDescription className="sr-only">
            Confirm moving active tasks back to storage
          </DialogDescription>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowClearAllDialog(false); setClearAllListId(null); }}>
              Cancel
            </Button>
            <Button onClick={confirmClearAll} style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}>
              Clear All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </main>
  );
}
